import { DefaultMap, definedMap } from "@glideapps/ts-necessities";

import type { BasePrimitiveValue } from "../../common/components/computation-types";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { logError, logInfo } from "../../common/debug-print";
import { assertNever, defined, tokenizeTemplateString } from "../../common/Support";
import type { CompiledJSCallbacks, FetchingBuilder } from "../components";
import type { Formula, TableColumn, TableGlideType } from "../../common/description";
import {
    BinaryPredicateFormulaOperator,
    UnaryPredicateFormulaOperator,
    isComputedColumn,
    isRelationType,
} from "../../common/description";
import type { CodeGenerator, InternalCompileResult } from "./compiler";
import { compileFormulaWithCodeGenerator, getEffectiveDisplayFormulaForColumn } from "./compiler";
import { BinaryMathFunction } from "../../common/formula";

function iife(x: string): string {
    return `((function () { ${x} })())`;
}

function cb(n: keyof CompiledJSCallbacks): string {
    return `callbacks.${n}`;
}

function binaryMath(left: string, right: string, _canBeUndefined: false, f: (a: string, b: string) => string): string {
    return iife(`const a = ${cb("asMaybeNumber")}(${left});
    const b = ${cb("asMaybeNumber")}(${right});
    if (a === undefined || b === undefined) return undefined;
    return ${cb("cleanNumber")}(${f("a", "b")});`);
}

function vpToValue(x: string): string {
    // ##compiledRecomputeShortcut:
    // The code we used to generate was this:
    //
    // if (vp instanceof ${cb("RecomputingValueProducer")}) {
    //     return vp.recompute(force);
    // } else {
    //     return force(vp);
    // }
    //
    // This only works if the VP in question only subscribes via `force`.
    // There are some, however, which do their own subscribing, in which case
    // we won't get notified of updates here.

    return iife(`const vp = ${x};
    if (!${cb("isLoadedDefined")}(vp)) return vp;
    return force(vp);`);
}

interface Result {
    readonly s: string;
    readonly isVP: boolean;
}

function resultAsValue(r: Result): string {
    if (r.isVP) {
        return vpToValue(r.s);
    } else {
        return r.s;
    }
}

interface Props {
    readonly isVP: boolean;
}

class JSCodeGenerator implements CodeGenerator<Result, Props> {
    public readonly inlinedColumnFormats = true;
    public readonly mappedRowProps = { isVP: true };

    private readonly _issues: string[] = [];

    private _nameCounter = 0;
    private readonly _varNames = new DefaultMap<string, string>(() => "var" + (this._nameCounter++).toString());
    private readonly _ctxNames = new DefaultMap<string, string>(() => "ctx" + (this._nameCounter++).toString());

    private readonly _globals: [string, string][] = [];

    constructor(private readonly _builder: FetchingBuilder) {}

    private notImplemented(name: string): undefined {
        this._issues.push(`FIXME: not implemented: ${name}`);
        return undefined;
    }

    public get issues(): readonly string[] {
        return this._issues;
    }

    public getPropsFromResult(result: Result): Props {
        return result;
    }

    private makeTmpName(): string {
        return "tmp" + (this._nameCounter++).toString();
    }

    public makeVariableName(n: string): string {
        return this._varNames.get(n);
    }

    public makeContextVariableName(contextName: string | undefined): string {
        if (contextName === undefined) {
            return "outerCtx";
        } else {
            return this._ctxNames.get(contextName);
        }
    }

    private addGlobal(code: string): string {
        const varName = this.makeTmpName();
        this._globals.push([varName, code]);
        return varName;
    }

    public get globals(): readonly [string, string][] {
        return this._globals;
    }

    public constant(value: BasePrimitiveValue | null): Result {
        if (value === null) {
            return { s: "undefined", isVP: false };
        } else {
            return { s: JSON.stringify(value), isVP: false };
        }
    }

    public assignVariables(
        lets: readonly [string, InternalCompileResult<Result>][],
        body: InternalCompileResult<Result>
    ): Result {
        const assignments = lets.map(([lhs, rhs]) => `const ${lhs} = ${rhs.value.s};`);

        return {
            s: iife(`${assignments.join("\n")}
        return ${body.value.s};`),
            isVP: body.value.isVP,
        };
    }

    public getVariable(name: string, binding: InternalCompileResult<Result>): Result {
        return { s: name, isVP: binding.value.isVP };
    }

    public getColumn(
        column: TableColumn,
        _table: TableGlideType,
        row: Result,
        _forDataEditor: boolean
    ): Result | undefined {
        if (isRelationType(column.type) && !isComputedColumn(column)) {
            return this.notImplemented("getColumn with old-style relations");
        }

        if (row.isVP) {
            return {
                s: iife(`const r = ${row.s};
            if (!${cb("isLoadedDefined")}(r)) return r;
            const ins = r.getObjectInspector(force);
            if (!${cb("isLoadedDefined")}(ins)) return ins;
            return ins.getProperty(${JSON.stringify(column.name)});`),
                isVP: true,
            };
        } else {
            return {
                s: iife(`const co = ${cb("asObjectValue")}(${row.s});
            return co[${JSON.stringify(column.name)}];`),
                isVP: true,
            };
        }
    }

    public inContext(variable: string, body: InternalCompileResult<Result>): Result {
        return {
            s: iife(`const ${variable} = ctx;
        return ${body.value.s};`),
            isVP: body.value.isVP,
        };
    }

    public with(value: InternalCompileResult<Result>, context: InternalCompileResult<Result>): Result {
        return {
            s: `((function (ctx) {
            return ${value.value.s};
        })(${context.value.s}))`,
            isVP: value.value.isVP,
        };
    }

    public getContextVariable(name: string, isInner: boolean, props: Props): Result {
        if (isInner) {
            return { s: "ctx", isVP: props.isVP };
        } else {
            return { s: name, isVP: props.isVP };
        }
    }

    public ifThenElse(
        condition: InternalCompileResult<Result>,
        consequent: InternalCompileResult<Result>,
        alternative: InternalCompileResult<Result> | undefined
    ): Result {
        const isVP = consequent.value.isVP && alternative?.value.isVP !== false;
        let consequentString = consequent.value.s;
        let alternativeString = alternative?.value.s ?? "undefined";
        if (!isVP) {
            if (consequent.value.isVP) {
                consequentString = vpToValue(consequentString);
            }
            if (alternative?.value.isVP === true) {
                alternativeString = vpToValue(alternativeString);
            }
        }
        return {
            s: iife(`const cond = ${resultAsValue(condition.value)};
        if (!${cb("isLoadedDefined")}(cond)) return cond;
        if (cond !== false) {
            return ${consequentString};
        } else {
            return ${alternativeString};
        }`),
            isVP,
        };
    }

    public and(left: InternalCompileResult<Result>, right: InternalCompileResult<Result>): Result {
        return {
            s: iife(`const l = ${resultAsValue(left.value)};
        if (l === null) return null;
        if (l !== true) return false;
        const r = ${resultAsValue(right.value)};
        if (r === null) return null;
        return r === true;`),
            isVP: false,
        };
    }

    public not(value: InternalCompileResult<Result>): Result {
        return {
            s: iife(`const v = ${resultAsValue(value.value)};
        if (!${cb("isLoadedDefined")}(v)) return v;
        return !${cb("asBoolean")}(v);`),
            isVP: false,
        };
    }

    public binaryMath(
        op: BinaryMathFunction,
        left: InternalCompileResult<Result>,
        leftIsConstant: boolean,
        right: InternalCompileResult<Result>,
        rightIsConstant: boolean
    ): Result | undefined {
        const leftValue = resultAsValue(left.value);
        const rightValue = resultAsValue(right.value);
        switch (op) {
            case BinaryMathFunction.Add:
                if (leftIsConstant || rightIsConstant) {
                    return { s: binaryMath(leftValue, rightValue, false, (a, b) => `${a} + ${b}`), isVP: false };
                } else {
                    return {
                        s: iife(`const a = ${cb("asMaybeNumber")}(${leftValue});
                const b = ${cb("asMaybeNumber")}(${rightValue});
                if (a === undefined && b === undefined) return undefined;
                if (a === undefined) return b;
                if (b === undefined) return a;
                return ${cb("cleanNumber")}(a + b);`),
                        isVP: false,
                    };
                }

            case BinaryMathFunction.Subtract:
                if (leftIsConstant) {
                    return { s: binaryMath(leftValue, rightValue, false, (a, b) => `${a} - ${b}`), isVP: false };
                } else {
                    return {
                        s: iife(`const a = ${cb("asMaybeNumber")}(${leftValue});
                const b = ${cb("asMaybeNumber")}(${rightValue});
                if (a === undefined) return undefined;
                if (b === undefined) return a;
                return ${cb("cleanNumber")}(a - b);`),
                        isVP: false,
                    };
                }

            default:
                return this.notImplemented(`binaryMath ${op}`);
        }
    }

    public compareValues(
        op: BinaryPredicateFormulaOperator,
        left: InternalCompileResult<Result>,
        right: InternalCompileResult<Result>
    ): Result | undefined {
        const leftValue = resultAsValue(left.value);
        const rightValue = resultAsValue(right.value);
        switch (op) {
            case BinaryPredicateFormulaOperator.Equals: {
                return {
                    s: iife(`const l = ${leftValue};
                if (l === null) return null;
                const r = ${rightValue};
                if (r === null) return null;
                return ${cb("areValuesEqual")}(l, r);`),
                    isVP: false,
                };
            }

            case BinaryPredicateFormulaOperator.ContainsString: {
                return {
                    s: iife(`const l = ${leftValue};
                if (l === null) return null;
                const r = ${rightValue};
                if (r === null) return null;
                return ${cb("asString")}(l).toLowerCase().includes(${cb("asString")}(r).toLowerCase());`),
                    isVP: false,
                };
            }

            default:
                return this.notImplemented(`compareValues ${op}`);
        }
    }

    public checkValue(op: UnaryPredicateFormulaOperator, value: InternalCompileResult<Result>): Result {
        switch (op) {
            case UnaryPredicateFormulaOperator.IsNotEmpty:
                return {
                    s: iife(`const v = ${resultAsValue(value.value)};
                    if (v === null) return null;
                    return ${cb("isValueNotEmpty")}(v);`),
                    isVP: false,
                };

            default:
                return assertNever(op);
        }
    }

    public constantTextTemplate(template: string, replacements: ReadonlyMap<string, Result>): Result {
        const definitions: string[] = [];

        const names = new Map<string, string>();
        for (const [v, replacement] of replacements.entries()) {
            const n = this.makeTmpName();
            names.set(v, n);
            definitions.push(`const ${n} = ${cb("asString")}(${resultAsValue(replacement)});`);
        }

        const tokens = tokenizeTemplateString(template, Array.from(replacements.keys()));
        const parts: string[] = [];
        for (const t of tokens) {
            if (t.isText) {
                parts.push(JSON.stringify(t.text));
            } else {
                parts.push(defined(names.get(t.value)));
            }
        }

        return {
            s: iife(`${definitions.join("\n")}
        return ${parts.join(" + ")};`),
            isVP: false,
        };
    }

    public splitString(string: InternalCompileResult<Result>, separator: InternalCompileResult<Result>): Result {
        return {
            s: iife(`const str = ${resultAsValue(string.value)};
        if (str === null) return null;
        const sep = ${resultAsValue(separator.value)};
        if (sep === null) return null;
        if (str === undefined || sep === undefined) return undefined;
        return ${cb("asString")}(str)
            .split(${cb("asString")}(sep))
            .map(s => s.trim())
            .filter(s => s !== "")
            .map(s => new ${cb("Constant")}(s));`),
            isVP: false,
        };
    }

    public withDefaultFalse(value: InternalCompileResult<Result>): Result {
        return {
            s: iife(`const v = ${resultAsValue(value.value)};
        if (!${cb("isLoadedDefined")}(v)) return false;
        return v;`),
            isVP: false,
        };
    }

    public applyColumnFormat(
        value: InternalCompileResult<Result>,
        table: TableGlideType,
        column: TableColumn
    ): Result | undefined {
        const displayFormula = getEffectiveDisplayFormulaForColumn(this._builder, table, column);
        if (displayFormula !== undefined) {
            logError("Cannot compile display formula to JS", displayFormula);
            return this.notImplemented("applyColumnFormat");
        }

        return value.value;
    }

    public constantFormatNumberFixed?(
        value: InternalCompileResult<Result>,
        decimalsAfterPoint: number,
        groupSeparator: boolean,
        currency: string | undefined
    ): Result {
        const formatter = this.addGlobal(
            `${cb("makeFixedNumberFormatter")}(${JSON.stringify(decimalsAfterPoint)}, ${JSON.stringify(
                groupSeparator
            )}, ${definedMap(currency, JSON.stringify) ?? "undefined"})`
        );
        return {
            s: iife(`const v = ${resultAsValue(value.value)};
        if (v === null) return null;
        const n = ${cb("asMaybeNumber")}(v);
        if (n === undefined) return undefined;
        const r = ${formatter}.format(n);
        return ${currency === "USD" ? `${cb("addUSD")}(r)` : "r"};
        `),
            isVP: false,
        };
    }

    public formatNumberFixed(
        value: InternalCompileResult<Result>,
        decimalsAfterPoint: InternalCompileResult<Result>,
        groupSeparator: InternalCompileResult<Result>,
        currency: InternalCompileResult<Result> | undefined
    ): Result {
        return {
            s: iife(`const v = ${resultAsValue(value.value)};
        const dap = ${resultAsValue(decimalsAfterPoint.value)};
        const gs = ${resultAsValue(groupSeparator.value)};
        const cur = ${definedMap(currency?.value, resultAsValue) ?? "undefined"};
        if (v === null || dap === null) return null;
        const n = ${cb("asMaybeNumber")}(v);
        if (n === undefined) return undefined;
        if (dap === undefined) return n;
        return ${cb("formatNumberFixed")}(
            n,
            ${cb("asNumber")}(dap),
            ${cb("definedMap")}(gs, ${cb("asBoolean")}) || false,
            ${cb("loadedDefinedMap")}(cur, ${cb("asString")})
        );`),
            isVP: false,
        };
    }
}

export function compileFormulaToJavaScript(
    builder: FetchingBuilder,
    outerTable: TableGlideType | undefined,
    formula: Formula,
    forDataEditor: boolean,
    uniqueID: string,
    primitiveName: string
): SerializedComponent | undefined {
    const codegen = new JSCodeGenerator(builder);
    const result = compileFormulaWithCodeGenerator(
        codegen,
        builder,
        outerTable,
        { isVP: true },
        formula,
        forDataEditor,
        uniqueID
    );

    if (!result.success) {
        builder.info("Could not compile formula to JS - falling back to regular formulas", result);
        return undefined;
    }

    const classCode = `class ${primitiveName} extends ${cb("RecomputingValueProducer")} {
        constructor(ctx) {
            super();
            this.ctx = ctx;
        }
        recompute(force) {
            try {
                if (currentlyRecomputing) {
                    return ${cb("detectedRecursiveComputation")}();
                }
                currentlyRecomputing = true;
                const ctx = this.ctx;
                return ${resultAsValue(result.value)};
            } finally {
                currentlyRecomputing = false;
            }
        }    
    }`;
    const evaluableCode = `class ${primitiveName}Evaluable {
        evalInEnvironment(env, ctx) {
            return new ${primitiveName}(ctx);
        }

        static get spec() {
            return [${JSON.stringify(primitiveName)}, "evaluable"];
        }
    }`;
    const functionCode = `(function (callbacks) {
        let currentlyRecomputing = false;
        ${codegen.globals.map(([n, v]) => `const ${n} = ${v};`).join("\n")}
        ${classCode}
        ${evaluableCode}
        ${cb("registerDeserializable")}(${primitiveName}Evaluable);
    })`;

    // logInfo("new formula", primitiveName, formula, functionCode);

    if (functionCode.length > 50000) {
        // Hoping this will prevent ##compiledJSStackOverflow in most cases.
        // The last case I've seen was 350+KB.
        logInfo("Compiled JS code too big - falling back to regular formulas");
        return undefined;
    }

    const serializedComponent = builder.b.primitive(primitiveName, {});
    try {
        builder.setCompiledJavaScript?.(primitiveName, functionCode, serializedComponent);
    } catch (e: unknown) {
        logError("Setting compiled JavaScript failed - falling back to regular formulas", e);
        return undefined;
    }
    return serializedComponent;
}
